/* eslint-disable max-lines-per-function */
/* eslint-disable max-lines */
import type Konva from "konva";
import type { RefObject } from "react";
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { Group, Image, Transformer, Line } from "react-konva";

import { trackResizeStarted } from "@design-lab/shared/lib/analytics";
import type { PlacementFormPosition } from "@design-lab/shared/types";

import { RemoveIcon, BOX_SIZE } from "./RemoveIcon";
import { TextLabel } from "./SizeLabel";
import { artworkGroupAnchorStyleFunc } from "../lib";

export type ArtworkDimensions = {
  x: number;
  y: number;
  width: number;
  height: number;
};

const LABEL_OFFSET = 8;

type DraggingLineProps = {
  printableArea: { left: number; top: number; width: number; height: number };
  isCentered: boolean;
  zoom: number;
};

function DraggingLine({ printableArea, isCentered, zoom }: DraggingLineProps) {
  return (
    <>
      <Line
        points={[
          printableArea.left + printableArea.width / 2,
          printableArea.top,
          printableArea.left + printableArea.width / 2,
          printableArea.top + printableArea.height
        ]}
        stroke={isCentered ? "#00A65F" : "#99A1AF"}
        strokeWidth={1}
        dash={isCentered ? [] : [4, 4]}
        listening={false}
      />
      {isCentered && (
        <TextLabel
          x={printableArea.left + printableArea.width / 2 - 26 / zoom}
          y={printableArea.top + 10}
          tagFill="#00A65F"
          textFill="#FFFFFF"
        >
          Centered
        </TextLabel>
      )}
    </>
  );
}

type SizeLabelsProps = {
  artworkDimensions: ArtworkDimensions;
  showWidthLabel: boolean;
  showHeightLabel: boolean;
  labelsOffset: number;
  roundedWidth: number;
  roundedHeight: number;
  sizeLabelTagFill: string;
  sizeLabelTextFill: string;
  widthLabelRef: RefObject<Konva.Label | null>;
  heightLabelRef: RefObject<Konva.Label | null>;
};

function SizeLabels({
  artworkDimensions,
  showWidthLabel,
  showHeightLabel,
  labelsOffset,
  roundedWidth,
  roundedHeight,
  sizeLabelTagFill,
  sizeLabelTextFill,
  widthLabelRef,
  heightLabelRef
}: SizeLabelsProps) {
  return (
    <>
      {showWidthLabel && (
        <TextLabel
          ref={widthLabelRef}
          x={artworkDimensions.x + artworkDimensions.width / 2}
          y={artworkDimensions.y + artworkDimensions.height + labelsOffset}
          tagFill={sizeLabelTagFill}
          textFill={sizeLabelTextFill}
        >
          {`${roundedWidth}"`}
        </TextLabel>
      )}
      {showHeightLabel && (
        <TextLabel
          ref={heightLabelRef}
          x={artworkDimensions.x - labelsOffset}
          y={artworkDimensions.y + artworkDimensions.height / 2}
          tagFill={sizeLabelTagFill}
          textFill={sizeLabelTextFill}
        >
          {`${roundedHeight}"`}
        </TextLabel>
      )}
    </>
  );
}

export type ArtworkGroupProps = {
  artworkId: string;
  placement: PlacementFormPosition;
  artworkImage?: HTMLImageElement | undefined;
  artworkDimensions?: ArtworkDimensions | undefined;
  readonly: boolean;
  draggable: boolean;
  isSelected: boolean;
  isHovered: boolean;
  printableArea?: { left: number; top: number; width: number; height: number } | undefined;
  ppi: number;
  scale: number;
  zoom: number;
  roundedWidth: number;
  roundedHeight: number;
  showWidthLabel: boolean;
  showHeightLabel: boolean;
  showRemoveButton?: boolean;
  sizeLabelTagFill: string;
  sizeLabelTextFill: string;
  showResizeButton?: boolean;
  onSelect: () => void;
  onDragStart?: () => void;
  onDragEnd?: (params: { x: number; y: number; width: number; height: number }) => boolean;
  onDragMove?: (params: { x: number; y: number; width: number; height: number }) => void;
  onRemove?: (() => void) | undefined;
  onResize: (widthInches: number, heightInches: number) => void;
  onHover: (isHovered: boolean) => void;
};

export function ArtworkGroup({
  artworkId,
  placement,
  artworkImage,
  artworkDimensions,
  readonly,
  draggable,
  isSelected,
  isHovered,
  onSelect,
  onDragStart,
  onDragEnd,
  onDragMove,
  printableArea,
  ppi,
  scale,
  zoom,
  roundedWidth,
  roundedHeight,
  showWidthLabel,
  showHeightLabel,
  sizeLabelTagFill,
  sizeLabelTextFill,
  onRemove,
  showRemoveButton = true,
  showResizeButton = true,
  onResize,
  onHover
}: ArtworkGroupProps) {
  const imageRef = useRef<Konva.Image>(null);
  const groupRef = useRef<Konva.Group>(null);
  const transformerRef = useRef<Konva.Transformer>(null);
  const widthLabelRef = useRef<Konva.Label>(null);
  const heightLabelRef = useRef<Konva.Label>(null);
  const infoHighlighterRef = useRef<Konva.Label>(null);
  const infoHighlighterDesignTabRef = useRef<Konva.Label>(null);

  const [isDragging, setIsDragging] = useState(false);
  const [isCentered, setIsCentered] = useState(false);

  const boundBoxFunc = useCallback(
    (oldBox: any, newBox: any) => {
      if (!artworkImage || !printableArea) return oldBox;

      const aspectRatio = artworkImage.height / artworkImage.width;
      const minSize = 20;

      const stageScale = imageRef.current?.getStage()?.scaleX() ?? 1;

      const maxWidth = printableArea.width * stageScale;
      const maxHeight = printableArea.height * stageScale;
      const maxAllowedWidth = Math.min(maxWidth, maxHeight / aspectRatio);

      const constrainedWidth = Math.max(minSize, Math.min(newBox.width, maxAllowedWidth));
      const constrainedHeight = constrainedWidth * aspectRatio;

      const x = printableArea.left * stageScale + (maxWidth - constrainedWidth) / 2;
      const y = printableArea.top * stageScale;

      return {
        ...newBox,
        width: constrainedWidth,
        height: constrainedHeight,
        x,
        y
      };
    },
    [artworkImage, printableArea]
  );

  const handleTransform = useCallback(() => {
    if (!imageRef.current || !printableArea) return;

    const node = imageRef.current;

    const scaledWidth = node.width() * node.scaleX();
    const scaledHeight = node.height() * node.scaleY();

    const widthInches = scaledWidth / scale / ppi;
    const heightInches = scaledHeight / scale / ppi;

    const roundedWidthInches = Math.round(widthInches * 100) / 100;
    const roundedHeightInches = Math.round(heightInches * 100) / 100;

    const newWidth = roundedWidthInches * ppi * scale;
    const newHeight = roundedHeightInches * ppi * scale;

    node.width(newWidth);
    node.height(newHeight);
    node.scaleX(1);
    node.scaleY(1);

    node.x(printableArea.left + (printableArea.width - newWidth) / 2);
    node.y(printableArea.top);

    onResize(roundedWidthInches, roundedHeightInches);
  }, [onResize, printableArea, ppi, scale]);

  const handleTransformStart = useCallback(() => {
    if (!imageRef.current || !printableArea) return;

    const side = placement === "back" ? "back" : "front";

    trackResizeStarted({
      placement: side,
      artworkId,
      width: imageRef.current.width(),
      height: imageRef.current.height()
    });
  }, [artworkId, placement, printableArea]);

  const handleSelectEvent = useCallback(
    (e: Konva.KonvaEventObject<MouseEvent | TouchEvent>) => {
      if (readonly || !imageRef.current) return;
      e.cancelBubble = true;
      onSelect();
    },
    [readonly, onSelect]
  );

  const handleDragStart = useCallback(() => {
    if (!draggable) return;
    setIsDragging(true);
    onDragStart?.();
  }, [draggable, onDragStart]);

  const handleDragMove = useCallback(() => {
    if (!draggable || !imageRef.current || !printableArea) return;
    const node = imageRef.current;

    // Snap logic
    const centerX = printableArea.left + printableArea.width / 2;
    const nodeWidth = node.width() * node.scaleX();
    const nodeCenterX = node.x() + nodeWidth / 2;

    const SNAP_THRESHOLD = 10;

    if (Math.abs(nodeCenterX - centerX) < SNAP_THRESHOLD) {
      const snappedX = centerX - nodeWidth / 2;
      node.x(snappedX);
      setIsCentered(true);
    } else {
      setIsCentered(false);
    }

    onDragMove?.({
      x: node.x(),
      y: node.y(),
      width: node.width() * node.scaleX(),
      height: node.height() * node.scaleY()
    });
  }, [draggable, onDragMove, printableArea]);

  const handleDragEnd = useCallback(() => {
    if (!draggable || !imageRef.current) return;
    const node = imageRef.current;
    setIsDragging(false);
    setIsCentered(false);
    const placementChanged = onDragEnd?.({
      x: node.x(),
      y: node.y(),
      width: node.width() * node.scaleX(),
      height: node.height() * node.scaleY()
    });
    if (!placementChanged) {
      handleTransform();
    }
  }, [draggable, onDragEnd, handleTransform]);

  // Configure image smoothing on layer canvas context to prevent aliasing artifacts
  useEffect(() => {
    if (!imageRef.current) return;

    const layer = imageRef.current.getLayer();
    if (!layer) return;

    const canvas = layer.getCanvas() as unknown as HTMLCanvasElement;
    const ctx = canvas.getContext("2d");
    if (ctx) {
      ctx.imageSmoothingEnabled = true;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      if ("imageSmoothingQuality" in ctx) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (ctx as any).imageSmoothingQuality = "high";
      }
    }
  }, [artworkImage, artworkDimensions, placement]);

  // Sync node position when artworkDimensions or placement changes
  // This ensures the node is positioned correctly after a placement change,
  // as Konva's drag operation may have modified the node's internal position
  useEffect(() => {
    if (!imageRef.current || !artworkDimensions) return;

    const node = imageRef.current;
    node.x(artworkDimensions.x);
    node.y(artworkDimensions.y);
    node.width(artworkDimensions.width);
    node.height(artworkDimensions.height);
    node.getLayer()?.batchDraw();
  }, [artworkDimensions, placement]);

  // Manage transformer nodes based on selection state
  useEffect(() => {
    if (!transformerRef.current) return;

    if ((isSelected || isHovered) && !readonly && imageRef.current) {
      transformerRef.current.nodes([imageRef.current]);
    } else {
      transformerRef.current.nodes([]);
    }
    transformerRef.current.getLayer()?.batchDraw();
  }, [isSelected, isHovered, readonly, artworkImage, artworkDimensions]);

  // Adjust label offsets when selected or zoom changes
  useLayoutEffect(() => {
    if (!isSelected || readonly) return;

    const stageScale = widthLabelRef.current?.getStage()?.scaleX() ?? 1;

    if (widthLabelRef.current) {
      const rect = widthLabelRef.current.getClientRect();
      widthLabelRef.current.offsetX(rect.width / 2 / stageScale);
    }
    if (heightLabelRef.current) {
      const rect = heightLabelRef.current.getClientRect();
      heightLabelRef.current.offsetX(rect.width / stageScale);
      heightLabelRef.current.offsetY(rect.height / 2 / stageScale);
    }
  }, [isSelected, artworkDimensions, readonly, zoom]);

  // Center non-design tab info highlighter horizontally (zoom-aware)
  useLayoutEffect(() => {
    [infoHighlighterDesignTabRef.current, infoHighlighterRef.current].forEach(label => {
      if (label) {
        const stageScale = label.getStage()?.scaleX() ?? 1;
        const rect = label.getClientRect();
        label.offsetX(rect.width / 2 / stageScale);
      }
    });
  }, [artworkDimensions, zoom, isSelected]);

  const labelsOffset = LABEL_OFFSET / zoom;
  const showIcon = artworkImage && artworkDimensions && isSelected;

  return (
    <Group ref={groupRef}>
      {artworkImage && artworkImage.width > 0 && artworkImage.height > 0 && artworkDimensions && (
        <Image
          ref={imageRef}
          image={artworkImage}
          x={artworkDimensions.x}
          y={artworkDimensions.y}
          width={artworkDimensions.width}
          height={artworkDimensions.height}
          draggable={draggable}
          onMouseEnter={() => onHover(true)}
          onMouseLeave={() => onHover(false)}
          onClick={handleSelectEvent}
          onTap={handleSelectEvent}
          onDragStart={handleDragStart}
          onDragMove={handleDragMove}
          onDragEnd={handleDragEnd}
          onTransform={handleTransform}
          onTransformStart={handleTransformStart}
        />
      )}
      {artworkImage && artworkImage.width > 0 && artworkImage.height > 0 && artworkDimensions && (
        <Transformer
          ref={transformerRef}
          visible={true}
          rotateEnabled={false}
          flipEnabled={false}
          keepRatio={true}
          boundBoxFunc={boundBoxFunc}
          borderStroke="#647CF7"
          borderDash={[4, 4]}
          borderStrokeWidth={1}
          enabledAnchors={showResizeButton && !isHovered ? ["bottom-left"] : []}
          anchorStroke="#D1D5DC"
          anchorSize={Math.max(BOX_SIZE / (zoom || 1), 24)}
          anchorCornerRadius={8}
          centeredScaling={false}
          ignoreStroke={true}
          anchorStyleFunc={anchor => artworkGroupAnchorStyleFunc(anchor, zoom)}
        />
      )}

      {isDragging && printableArea && (
        <DraggingLine printableArea={printableArea} isCentered={isCentered} zoom={zoom} />
      )}
      {artworkImage && artworkDimensions && (
        <SizeLabels
          artworkDimensions={artworkDimensions}
          showWidthLabel={showWidthLabel}
          showHeightLabel={showHeightLabel}
          labelsOffset={labelsOffset}
          roundedWidth={roundedWidth}
          roundedHeight={roundedHeight}
          sizeLabelTagFill={sizeLabelTagFill}
          sizeLabelTextFill={sizeLabelTextFill}
          widthLabelRef={widthLabelRef}
          heightLabelRef={heightLabelRef}
        />
      )}
      {showIcon && showRemoveButton && onRemove && (
        <RemoveIcon
          x={artworkDimensions.x + artworkDimensions.width + BOX_SIZE / 4 / zoom}
          y={artworkDimensions.y - BOX_SIZE / 4 / zoom}
          onRemove={onRemove}
        />
      )}
    </Group>
  );
}
